import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiArrowLeft,
  HiOutlineCalendar,
  HiOutlineMoon,
  HiOutlinePlus,
  HiOutlineSun,
  HiOutlineTrash,
} from 'react-icons/hi';
import { useSchool } from '../../context/SchoolContext';
import { useTheme } from '../../context/ThemeContext';
import { isReservedFieldName } from '../../utils/config';
import SuccessModal from '../../components/feedback/SuccessModal';
import { BRAND } from '../../utils/brand';

function Settings() {
  const navigate = useNavigate();
  const {
    school,
    sections,
    terms,
    currentTerm,
    startNextTerm,
    addSection,
    updateSectionFields,
  } = useSchool();
  const { theme, toggleTheme } = useTheme();
  const [newSectionName, setNewSectionName] = useState('');
  const [fieldInputs, setFieldInputs] = useState({});
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEndTermConfirm, setShowEndTermConfirm] = useState(false);
  const [successModal, setSuccessModal] = useState(null);

  const sortedSections = useMemo(() => sections || [], [sections]);
  const hasSchoolId = Boolean(
    localStorage.getItem('schoolId') || school?.id || sortedSections[0]?.school_id
  );

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;

    if (isReservedFieldName(newSectionName)) {
      setError('That section name is reserved for system use.');
      return;
    }

    setSaving(true);
    setError('');
    setStatus('');

    try {
      await addSection(newSectionName);
      setNewSectionName('');
      setStatus('Section added.');
      setSuccessModal({
        title: 'Section Added',
        message: `${newSectionName.trim()} is now available in the dashboard.`,
      });
    } catch (err) {
      console.error(err);
      setError(
        err?.message === 'School ID is missing.'
          ? 'School setup is incomplete. Go back through onboarding so new sections can be linked to this school.'
          : err?.message || 'Could not add section.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = async (section) => {
    const fieldName = (fieldInputs[section.id] || '').trim();
    if (!fieldName) return;

    if (isReservedFieldName(fieldName)) {
      setError('ID, record code, date, and time are system-generated and cannot be custom fields.');
      return;
    }

    if ((section.fields || []).some(field => field.toLowerCase() === fieldName.toLowerCase())) {
      setError('That field already exists in this section.');
      return;
    }

    setSaving(true);
    setError('');
    setStatus('');

    try {
      await updateSectionFields(section.id, [...(section.fields || []), fieldName]);
      setFieldInputs(current => ({ ...current, [section.id]: '' }));
      setStatus('Field added.');
      setSuccessModal({
        title: 'Field Added',
        message: `${fieldName} was added to ${section.name}.`,
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not add field.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveField = async (section, fieldName) => {
    setSaving(true);
    setError('');
    setStatus('');

    try {
      await updateSectionFields(
        section.id,
        (section.fields || []).filter(field => field !== fieldName)
      );
      setStatus('Field removed. Existing records keep their saved data.');
      setSuccessModal({
        title: 'Field Removed',
        message: `${fieldName} was removed from ${section.name}. Existing records keep their saved data.`,
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not remove field.');
    } finally {
      setSaving(false);
    }
  };

  const handleStartNextTerm = async () => {
    setSaving(true);
    setError('');
    setStatus('');

    try {
      const newTerm = await startNextTerm();
      setShowEndTermConfirm(false);
      setStatus(`${formatTermLabel(newTerm)} is now the current term.`);
      setSuccessModal({
        title: 'New Term Started',
        message: `${formatTermLabel(newTerm)} is now the current active term.`,
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not start the next term.');
    } finally {
      setSaving(false);
    }
  };

  const nextTerm = currentTerm ? getNextTermDetails(currentTerm) : null;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 text-gray-900 dark:bg-neutral-950 dark:text-neutral-100 sm:px-6 lg:px-8">
      <SuccessModal
        title={successModal?.title}
        message={successModal?.message}
        onClose={() => setSuccessModal(null)}
      />
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            <HiArrowLeft />
            Dashboard
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex h-11 w-[72px] items-center rounded-full border border-gray-200 bg-white p-1 transition hover:bg-gray-50 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-full text-lg shadow-sm transition ${
                theme === 'dark'
                  ? 'translate-x-7 bg-neutral-100 text-neutral-900'
                  : 'translate-x-0 bg-neutral-800 text-white'
              }`}
            >
              {theme === 'dark' ? <HiOutlineMoon /> : <HiOutlineSun />}
            </span>
          </button>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Settings</p>
          <h1 className="mt-1 text-2xl font-bold">{school?.name || BRAND.name}</h1>
        </div>

        {(status || error) && (
          <div className={`rounded-xl border px-4 py-3 text-sm font-medium ${
            error
              ? 'border-red-100 bg-red-50 text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300'
              : 'border-green-100 bg-green-50 text-green-700 dark:border-green-800 dark:bg-neutral-900 dark:text-green-300'
          }`}>
            {error || status}
          </div>
        )}

        <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-blue-600 dark:text-neutral-200">
                <HiOutlineCalendar />
                <p className="text-xs font-bold uppercase tracking-wider">Academic Term</p>
              </div>
              <h2 className="mt-2 text-lg font-bold">
                {currentTerm ? formatTermLabel(currentTerm) : 'No active term'}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
                New records are saved under the current active term. Previous terms stay available from the dashboard term selector.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowEndTermConfirm(true)}
              disabled={saving || !currentTerm}
              className="inline-flex items-center justify-center rounded-xl bg-neutral-800 px-5 py-3 text-sm font-semibold text-white transition hover:bg-neutral-700 disabled:bg-gray-200 disabled:text-gray-400 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
            >
              End Term & Start New Term
            </button>
          </div>

          {terms?.length > 1 && (
            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Terms</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {terms.map(term => (
                  <span
                    key={term.id}
                    className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-gray-600 dark:bg-neutral-900 dark:text-neutral-300"
                  >
                    {formatTermLabel(term)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="text-lg font-bold">Add Section</h2>
          {!hasSchoolId && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 dark:border-blue-800 dark:bg-neutral-900 dark:text-blue-300">
              This school needs to finish onboarding before new sections can be added.
            </div>
          )}
          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <input
              value={newSectionName}
              onChange={(event) => setNewSectionName(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleAddSection()}
              placeholder="e.g. Students, Payroll, Expenses"
              className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            />
            <button
              onClick={handleAddSection}
              disabled={saving || !newSectionName.trim() || !hasSchoolId}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
            >
              <HiOutlinePlus />
              Add
            </button>
          </div>
        </section>

        <section className="space-y-4">
          {sortedSections.map(section => (
            <div key={section.id} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-neutral-200">
                    {section.initials}
                  </p>
                  <h3 className="text-lg font-bold">{section.name}</h3>
                </div>
                <p className="text-sm text-gray-400">{(section.fields || []).length} fields</p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {(section.fields || []).length === 0 && (
                  <p className="text-sm text-gray-400">No custom fields yet.</p>
                )}
                {(section.fields || []).map(field => (
                  <span
                    key={field}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 dark:bg-neutral-800 dark:text-neutral-200"
                  >
                    {field}
                    <button
                      type="button"
                      onClick={() => handleRemoveField(section, field)}
                      className="text-gray-400 transition hover:text-red-500"
                      aria-label={`Remove ${field}`}
                    >
                      <HiOutlineTrash />
                    </button>
                  </span>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-3 md:flex-row">
                <input
                  value={fieldInputs[section.id] || ''}
                  onChange={(event) => setFieldInputs(current => ({
                    ...current,
                    [section.id]: event.target.value,
                  }))}
                  onKeyDown={(event) => event.key === 'Enter' && handleAddField(section)}
                  placeholder={`Add field to ${section.name}`}
                  className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                />
                <button
                  onClick={() => handleAddField(section)}
                  disabled={saving || !(fieldInputs[section.id] || '').trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  <HiOutlinePlus />
                  Field
                </button>
              </div>
            </div>
          ))}
        </section>
      </div>

      {showEndTermConfirm && currentTerm && nextTerm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-800/50 px-4 py-6">
          <div className="w-full max-w-lg rounded-xl border border-gray-100 bg-white p-5 shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className="text-lg font-bold">End {formatTermLabel(currentTerm)}?</h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">
              This archives the current term and starts {nextTerm.name} {nextTerm.academicYear}. Existing records stay available in previous terms. New records will default to the new term.
            </p>

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowEndTermConfirm(false)}
                disabled={saving}
                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:text-gray-300 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStartNextTerm}
                disabled={saving}
                className="rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
              >
                {saving ? 'Starting...' : `Start ${nextTerm.name}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;

function formatTermLabel(term) {
  return `${term.name} ${term.academic_year}`;
}

function getNextTermDetails(term) {
  if (term.name === 'First Term') {
    return { name: 'Second Term', academicYear: term.academic_year };
  }

  if (term.name === 'Second Term') {
    return { name: 'Third Term', academicYear: term.academic_year };
  }

  const [start, end] = String(term.academic_year || '').split('/').map(value => Number(value));

  return {
    name: 'First Term',
    academicYear: start && end ? `${start + 1}/${end + 1}` : '2026/2027',
  };
}
